import { observer } from 'mobx-react';
import { Button, Space, Tooltip, Typography } from 'antd';
import {
  CloudServerOutlined,
  DollarCircleOutlined,
  GlobalOutlined,
  PlusOutlined,
  ShopOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { useStore } from '@/stores';
import { colors } from '@/theme/tokens';
import { SectionCard } from '@/components/SectionCard';
import { StatusPill } from '@/components/StatusPill';
import type { ModelFamily } from '@/stores/ModelsStore';
import type { ModelRow } from '@/api/models';

const { Text } = Typography;

// ─────────────────────────────────────────────────────────────────────────────
// One card per MODEL FAMILY (ai.AIModel.Name), with a row per deployment.
//
// The registry is 20 rows for 6 model names — `gpt-4.1` exists once per merchant.
// Rendering those as 20 peer rows reproduces the SSMS grid this page replaces.
// The family owns the identity (vendor, price, capabilities); each deployment row
// carries only what differs: scope, endpoint, availability, wiring.
//
// Nothing here composes a sentence about what a column means. Those strings come
// from ModelRegistryRules on the server, so this page, a CLI and a report can
// never describe the same row differently.
// ─────────────────────────────────────────────────────────────────────────────

interface Props {
  family: ModelFamily;
  onOpenRow: (row: ModelRow) => void;
  onEditRow: (row: ModelRow) => void;
  onSetRate: (family: ModelFamily) => void;
}

export const ModelFamilyCard = observer(function ModelFamilyCard({
  family,
  onOpenRow,
  onEditRow,
  onSetRate,
}: Props) {
  const { themeStore } = useStore();
  const isDark = themeStore.mode === 'dark';

  // The family's rate: read off the first row that carries one. Every row in a
  // family resolves the same rate (the key is the model name), so this is the
  // family's price, not one deployment's.
  const priced = family.rows.find((r) => r.pricing != null);
  const rate = priced?.pricing ?? null;

  return (
    <SectionCard
      noPadding
      title={
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
          <span
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 14.5,
              fontWeight: 600,
              color: isDark ? colors.slate[50] : colors.slate[900],
            }}
          >
            {family.name}
          </span>
          {family.provider && <StatusPill tone="slate">{family.provider}</StatusPill>}
          {family.activeCount === 0 && <StatusPill tone="slate">No active deployment</StatusPill>}
          {family.conflictCount > 0 && (
            <StatusPill tone={family.worstSeverity === 'Blocking' ? 'red' : 'amber'} icon={<WarningOutlined />}>
              {family.conflictCount} to look at
            </StatusPill>
          )}
        </span>
      }
      subtitle={
        <span style={{ fontSize: 12 }}>
          {family.rows.length} {family.rows.length === 1 ? 'deployment' : 'deployments'}
          {family.deploymentNames.length > 0 && (
            <>
              {' · slug '}
              <code style={{ fontSize: 11.5 }}>{family.deploymentNames.join(', ')}</code>
            </>
          )}
          {!family.endpointsAgree && (
            <>
              {' · '}
              <span style={{ color: colors.amber[600], fontWeight: 600 }}>
                deployments point at different endpoints
              </span>
            </>
          )}
        </span>
      }
      actions={
        <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
          <Metric label="agents" value={family.agentsWired} isDark={isDark} />
          <Metric label="calls · 30d" value={family.traffic30d} isDark={isDark} />
        </div>
      }
    >
      <RateStrip family={family} rate={rate} isDark={isDark} onSetRate={onSetRate} />

      <div>
        {family.rows.map((row) => (
          <DeploymentRow
            key={row.id}
            row={row}
            isDark={isDark}
            onOpen={() => onOpenRow(row)}
            onEdit={() => onEditRow(row)}
          />
        ))}
      </div>
    </SectionCard>
  );
});

// ── The price, and the Phase-1 affordance for managing it ──────────────────
//
// Deliberately loud when absent. A model with traffic and no rate does not
// merely lack a number — every call on it records EstimatedCostUsd = NULL and
// InsightsManager sums `?? 0m`, so the spend it generates is reported as $0.
// A quiet grey dash would hide that.

function RateStrip({
  family,
  rate,
  isDark,
  onSetRate,
}: {
  family: ModelFamily;
  rate: NonNullable<ModelRow['pricing']> | null;
  isDark: boolean;
  onSetRate: (family: ModelFamily) => void;
}) {
  const hasTraffic = family.traffic30d > 0;

  if (!rate) {
    return (
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
          padding: '10px 18px',
          background: isDark ? `${colors.amber[700]}22` : colors.amber[100],
          borderTop: `1px solid ${isDark ? colors.slate[700] : colors.slate[100]}`,
          borderBottom: `1px solid ${colors.amber[600]}44`,
        }}
      >
        <span
          style={{
            fontSize: 12.5,
            color: isDark ? colors.amber[100] : colors.amber[700],
            display: 'inline-flex',
            alignItems: 'center',
            gap: 8,
          }}
        >
          <DollarCircleOutlined />
          <span>
            <b>No rate on file.</b>{' '}
            {hasTraffic
              ? `${family.traffic30d.toLocaleString()} calls in the last 30 days recorded no cost, and reported spend counts them as $0.`
              : 'Any call on this model will record no cost.'}
          </span>
        </span>
        <Button size="small" type="primary" icon={<PlusOutlined />} onClick={() => onSetRate(family)}>
          Set a rate
        </Button>
      </div>
    );
  }

  // A rate per million tokens is not a price anyone can feel. Show what a
  // representative call costs alongside it.
  const perCall = (8_000 * rate.inputPer1MTokens + 1_000 * rate.outputPer1MTokens) / 1_000_000;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 12,
        flexWrap: 'wrap',
        padding: '9px 18px',
        background: isDark ? colors.slate[900] : colors.slate[50],
        borderTop: `1px solid ${isDark ? colors.slate[700] : colors.slate[100]}`,
        borderBottom: `1px solid ${isDark ? colors.slate[700] : colors.slate[100]}`,
      }}
    >
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', fontSize: 12.5 }}>
        <DollarCircleOutlined style={{ color: colors.slate[400] }} />
        <span style={{ fontFamily: "'JetBrains Mono', monospace", fontVariantNumeric: 'tabular-nums' }}>
          {money(rate.inputPer1MTokens)} in / {money(rate.outputPer1MTokens)} out
        </span>
        <Text type="secondary" style={{ fontSize: 12 }}>
          per 1M tokens · about {money(perCall)} for a typical 8K-in / 1K-out call
        </Text>
        {rate.matchedOn === 'DeploymentName' && (
          <Tooltip title="The rate row is keyed to this model's deployment slug rather than its name. Costing looks up PromptLog.ModelName, which holds the name — so verify a matching row exists under the name too.">
            <span>
              <StatusPill tone="amber">matched on slug</StatusPill>
            </span>
          </Tooltip>
        )}
      </span>
      <Space size={4}>
        <Text type="secondary" style={{ fontSize: 11.5 }}>
          since {new Date(rate.effectiveDate).toLocaleDateString()}
        </Text>
        <Button size="small" onClick={() => onSetRate(family)}>
          New rate
        </Button>
      </Space>
    </div>
  );
}

// ── One deployment ─────────────────────────────────────────────────────────

function DeploymentRow({
  row,
  isDark,
  onOpen,
  onEdit,
}: {
  row: ModelRow;
  isDark: boolean;
  onOpen: () => void;
  onEdit: () => void;
}) {
  const surfaces = [row.enableInChat && 'chat', row.enableInProcess && 'workflows'].filter(Boolean);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onOpen();
        }
      }}
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(140px, 1fr) minmax(200px, 1.4fr) minmax(150px, 1fr) 120px 92px',
        gap: 12,
        alignItems: 'center',
        padding: '11px 18px',
        borderBottom: `1px solid ${isDark ? colors.slate[800] : colors.slate[100]}`,
        cursor: 'pointer',
        opacity: row.active ? 1 : 0.72,
      }}
    >
      {/* Scope — NULL merchant is not "missing", it is every tenant. */}
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 7, minWidth: 0 }}>
        {row.merchantId == null ? (
          <GlobalOutlined style={{ color: colors.ink[600], fontSize: 12 }} />
        ) : (
          <ShopOutlined style={{ color: colors.slate[400], fontSize: 12 }} />
        )}
        <span style={{ fontSize: 12.5, fontWeight: row.merchantId == null ? 600 : 400 }}>
          {row.merchantId == null ? 'Every tenant' : `Merchant ${row.merchantId}`}
        </span>
        {/* A tenant can hold more than one row for the same model (merchant 100
            has two gpt-4.1 rows). Without the id they read as a duplicate. */}
        <Tooltip title={`ai.AIModel.Id ${row.id}`}>
          <code style={{ fontSize: 10, color: colors.slate[400] }}>#{row.id}</code>
        </Tooltip>
      </span>

      {/* Endpoint — the ApiId column, read as what it actually is. */}
      <EndpointCell row={row} isDark={isDark} />

      {/* Availability */}
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        {row.active ? <StatusPill tone="green">Active</StatusPill> : <StatusPill tone="slate">Off</StatusPill>}
        <Text type="secondary" style={{ fontSize: 11.5 }}>
          {surfaces.length > 0 ? surfaces.join(' + ') : 'no surface'}
        </Text>
      </span>

      {/* Wiring vs. use — two different questions, kept apart. */}
      <span style={{ fontSize: 11.5, color: isDark ? colors.slate[400] : colors.slate[500] }}>
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>
          {row.agentsWired.toLocaleString()} {row.agentsWired === 1 ? 'agent' : 'agents'}
        </span>
        <br />
        <span style={{ fontVariantNumeric: 'tabular-nums' }}>
          {row.traffic30d.toLocaleString()} calls
        </span>
      </span>

      {/* Conflicts + edit */}
      <span
        style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'flex-end', gap: 6 }}
        onClick={(e) => e.stopPropagation()}
      >
        {row.conflicts.length > 0 && (
          <Tooltip title={row.conflicts.map((c) => c.headline).join(' · ')}>
            <span>
              <StatusPill tone={worstTone(row)} icon={<WarningOutlined />}>
                {row.conflicts.length}
              </StatusPill>
            </span>
          </Tooltip>
        )}
        <Button size="small" type="text" onClick={onEdit}>
          Edit
        </Button>
      </span>
    </div>
  );
}

function EndpointCell({ row, isDark }: { row: ModelRow; isDark: boolean }) {
  if (!row.api) {
    return (
      <span style={{ fontSize: 12, color: colors.red[600] }}>
        <WarningOutlined /> ApiId {row.apiId} does not resolve
      </span>
    );
  }

  const path = row.api.useFoundryAgent
    ? 'Foundry Agent'
    : row.api.useResponsesApi
      ? 'Responses API'
      : 'Chat Completions';

  return (
    <span style={{ minWidth: 0 }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12.5 }}>
        <CloudServerOutlined
          style={{ fontSize: 12, color: row.api.isAzureHosted ? colors.green[600] : colors.amber[600] }}
        />
        <span style={{ fontWeight: 500 }}>{row.api.title}</span>
        {!row.api.isAzureHosted && (
          <Tooltip title="This endpoint is outside the Azure tenant, so prompts served by this row leave it. Confirm that is intended for the agents wired here.">
            <span>
              <StatusPill tone="amber">outside tenant</StatusPill>
            </span>
          </Tooltip>
        )}
      </span>
      <div
        style={{
          fontSize: 11,
          color: isDark ? colors.slate[500] : colors.slate[400],
          fontFamily: "'JetBrains Mono', monospace",
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}
      >
        {row.api.host} · {path}
      </div>
    </span>
  );
}

// ── bits ───────────────────────────────────────────────────────────────────

function Metric({ label, value, isDark }: { label: string; value: number; isDark: boolean }) {
  return (
    <span style={{ textAlign: 'right', lineHeight: 1.2 }}>
      <div
        style={{
          fontSize: 15,
          fontWeight: 700,
          fontVariantNumeric: 'tabular-nums',
          color: value > 0 ? (isDark ? colors.slate[100] : colors.slate[900]) : colors.slate[400],
        }}
      >
        {value.toLocaleString()}
      </div>
      <div style={{ fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.05em', color: colors.slate[400] }}>
        {label}
      </div>
    </span>
  );
}

function worstTone(row: ModelRow): 'red' | 'amber' | 'slate' {
  if (row.conflicts.some((c) => c.severity === 'Blocking')) return 'red';
  if (row.conflicts.some((c) => c.severity === 'Silent')) return 'amber';
  return 'slate';
}

/** Rates span $0.25 → $60. Keep cents for readable numbers, more places for small ones. */
export function money(value: number): string {
  if (value === 0) return '$0';
  return value < 0.01 ? `$${value.toFixed(4)}` : `$${value.toFixed(2)}`;
}
